import { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { ProtectedRoute } from '@/app/layouts/ProtectedRoute';
import { RoleRedirect } from '@/app/layouts/RoleRedirect';
import { ManagerRealtimeProvider } from '@/features/manager/ManagerRealtimeProvider';

const Login = lazy(() => import('@/app/pages/Login'));
const Signup = lazy(() => import('@/app/pages/Signup'));
const ForgotPassword = lazy(() => import('@/app/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/app/pages/ResetPassword'));
const SelectTech = lazy(() => import('@/app/pages/SelectTech'));
const Profile = lazy(() => import('@/app/pages/Profile'));
const Help = lazy(() => import('@/app/pages/Help'));
const NotFound = lazy(() => import('@/app/pages/NotFound'));
const TechHome = lazy(() => import('@/app/pages/tech/Home'));
const Diagnose = lazy(() => import('@/app/pages/tech/Diagnose'));
const Chat = lazy(() => import('@/app/pages/tech/Chat'));
const TruckHistory = lazy(() => import('@/app/pages/tech/TruckHistory'));
const TechFleet = lazy(() => import('@/app/pages/tech/Fleet'));
const TechNewWorkOrder = lazy(() => import('@/app/pages/tech/NewWorkOrder'));
const TechScanWorkOrder = lazy(() => import('@/app/pages/tech/ScanWorkOrder'));
const TechShiftHandoff = lazy(() => import('@/app/pages/tech/ShiftHandoff'));
const TechMyWork = lazy(() => import('@/app/pages/tech/MyWork'));
const TechWorkOrderView = lazy(() => import('@/app/pages/tech/WorkOrderView'));
const TruckProfile = lazy(() => import('@/app/pages/trucks/Profile'));
const ManagerHome = lazy(() => import('@/app/pages/manager/Home'));
const ManagerReadyToBill = lazy(() => import('@/app/pages/manager/ReadyToBill'));
const ManagerSchedule = lazy(() => import('@/app/pages/manager/Schedule'));
const ManagerBookJob = lazy(() => import('@/app/pages/manager/BookJob'));
const ManagerShopBoard = lazy(() => import('@/app/pages/manager/ShopBoard'));
const ManagerCompletedThreeCs = lazy(() => import('@/app/pages/manager/CompletedThreeCs'));
const ManagerWorkOrderDetail = lazy(() => import('@/app/pages/manager/WorkOrderDetail'));
const ManagerFleet = lazy(() => import('@/app/pages/manager/Fleet'));
const ManagerTrends = lazy(() => import('@/app/pages/manager/Trends'));
const ManagerFlags = lazy(() => import('@/app/pages/manager/Flags'));
const ManagerNeedsYou = lazy(() => import('@/app/pages/manager/NeedsYou'));
const ManagerShopSetup = lazy(() => import('@/app/pages/manager/ShopSetup'));
const ManagerAssistanceFlags = lazy(() => import('@/app/pages/manager/AssistanceFlags'));
const ManagerApprovals = lazy(() => import('@/app/pages/manager/Approvals'));
const ManagerTeam = lazy(() => import('@/app/pages/manager/Team'));
const ManagerTimeThresholds = lazy(() => import('@/app/pages/manager/TimeThresholds'));
const ManagerChannels = lazy(() => import('@/app/pages/manager/Channels'));
const ManagerCustomers = lazy(() => import('@/app/pages/manager/Customers'));
const ManagerEstimateDesk = lazy(() => import('@/app/pages/manager/EstimateDesk'));
const ManagerCustomerDetail = lazy(() => import('@/app/pages/manager/CustomerDetail'));
const ManagerEnrichSync = lazy(() => import('@/app/pages/manager/EnrichSync'));
const ManagerRoldi = lazy(() => import('@/app/pages/manager/RoldiManager'));
const ManagerRoldiQueue = lazy(() => import('@/app/pages/manager/RoldiQueue'));
const ManagerTeachRoldi = lazy(() => import('@/app/pages/manager/TeachRoldi'));
const AdminDashboard = lazy(() => import('@/app/pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('@/app/pages/admin/Users'));
const AdminPlatform = lazy(() => import('@/app/pages/admin/Platform'));
const AdminCosts = lazy(() => import('@/app/pages/admin/Costs'));
const AdminMetering = lazy(() => import('@/app/pages/admin/Metering'));
const AdminFlags = lazy(() => import('@/app/pages/admin/Flags'));
const AdminAudit = lazy(() => import('@/app/pages/admin/AuditLog'));
const AdminIntegrations = lazy(() => import('@/app/pages/admin/Integrations'));
const AdminPricing = lazy(() => import('@/app/pages/admin/Pricing'));
const AdminShops = lazy(() => import('@/app/pages/admin/Shops'));
const AdminRoldiStudio = lazy(() => import('@/app/pages/admin/roldi/Studio'));
const AdminKb = lazy(() => import('@/app/pages/admin/Kb'));
const AdminGold = lazy(() => import('@/app/pages/admin/GoldExamples'));
const AdminShifts = lazy(() => import('@/app/pages/admin/Shifts'));
const AdminSettings = lazy(() => import('@/app/pages/admin/Settings'));
const AdminAgents = lazy(() => import('@/app/pages/admin/Agents'));
const AdminDemoTraining = lazy(() => import('@/app/pages/admin/DemoTraining'));
const LeadershipOverview = lazy(() => import('@/app/pages/leadership/Overview'));
const LeadershipAnalytics = lazy(() => import('@/app/pages/leadership/Analytics'));

export function AppRoutes() {
  return (
    <Routes>
      {/* Root — redirect to role home or login. */}
      <Route index element={<RoleRedirect />} />

      {/* Auth */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        {/* WP7 Phase 2 — public, join-code-gated self-serve signup. */}
        <Route path="/signup" element={<Signup />} />
        {/* WP7 Phase 4 — self-serve password reset (provider recovery mail). */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/select-tech" element={<SelectTech />} />
      </Route>

      {/* Profile (any signed-in role) */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Tech */}
      {/*
        The in-app manual. Open to every signed-in role — the page filters its
        own chapters by role, so a technician never sees the admin book. Both
        the index and the per-chapter view render the same component.
      */}
      <Route
        path="/help"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <Help />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help/:chapterId"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <Help />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tech"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TechHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tech/diagnose"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <Diagnose />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tech/chat"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tech/truck/:truckId/history"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TruckHistory />
          </ProtectedRoute>
        }
      />
      {/* Sprint E E2 — tech-side fleet browse, tech role only. */}
      <Route
        path="/tech/fleet"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TechFleet />
          </ProtectedRoute>
        }
      />
      {/* Sprint L L3 (2026-05-13) — tech-side WO creation. Truck +
          voice-dictated complaint + Enrich WO# → project + initial task,
          then lands on Diagnose where Tech Roldi v20 may fan out to
          multi-task via create_task_from_complaint. */}
      <Route
        path="/tech/wo/new"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TechNewWorkOrder />
          </ProtectedRoute>
        }
      />
      {/* 2026-08-05 — scan the work order Enrich printed. Photo or PDF →
          every printed line comes back as its own pre-populated task,
          carrying Enrich's own line number and op code. Reviewed by a
          human before anything is written. */}
      <Route
        path="/tech/wo/scan"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TechScanWorkOrder />
          </ProtectedRoute>
        }
      />
      {/* Sprint M M5 (2026-05-14) — shift-handoff read surface. The
          incoming tech sees the most recent handoff from another tech
          at their primary_location_id; their own historical handoffs
          land in a separate list further down the page. */}
      <Route
        path="/tech/shift-handoff"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TechShiftHandoff />
          </ProtectedRoute>
        }
      />
      {/* Shane's Cut WS-G (2026-06-14) — "my work by day": the tech's own
          completed tasks grouped by day, powered by the WS-A completed_by
          stamp. */}
      <Route
        path="/tech/my-work"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TechMyWork />
          </ProtectedRoute>
        }
      />
      {/* WS-1 (2026-06-14) — tech-facing Work Order view. Standalone screen
          listing every task line on a WO; each row opens that line's existing
          single-task diagnose flow (/tech/diagnose?taskId=…). :projectId is
          the WO anchor; the server aggregates the full sibling set. */}
      <Route
        path="/tech/work-orders/:projectId"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TechWorkOrderView />
          </ProtectedRoute>
        }
      />

      {/* Sprint E E1 — shared truck profile, all signed-in roles
          (server-side scoping in trpc.trucks.* enforces per-role
          access; tech sees only accessible-location trucks). */}
      <Route
        path="/trucks/:truckId"
        element={
          <ProtectedRoute allowedRoles={['technician', 'manager', 'admin', 'leadership']}>
            <TruckProfile />
          </ProtectedRoute>
        }
      />

      {/* Manager — every manager-scoped route mounts ManagerRealtimeProvider
          so the Phase 8 Realtime subscription is alive whenever the
          manager is on a manager surface. */}
      <Route
        path="/manager"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerHome />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* The shop board — what is on the floor right now. The read behind
          Lisa's get_ops_snapshot, given a screen. */}
      <Route
        path="/manager/board"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerShopBoard />
          </ProtectedRoute>
        }
      />
      {/* Booked jobs (2026-10-08): the schedule, and booking a new one. Reading
          is manager/admin/leadership; the procedures gate the writes. */}
      <Route
        path="/manager/schedule"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/schedule/new"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <ManagerBookJob />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/ready"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerReadyToBill />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* Shane's Cut B — B3: completed-3C "port-to-Enrich" reference archive. */}
      <Route
        path="/manager/completed-3cs"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerCompletedThreeCs />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/work-orders/:taskId"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerWorkOrderDetail />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/fleet"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerFleet />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* WS-5 (2026-06-14) — manager fleet-trends (read-only analytics). */}
      <Route
        path="/manager/trends"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerTrends />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/flags"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerFlags />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* The rail's four attention rows, collapsed into one triage surface.
          Every queue it names keeps its own route and its own page — this
          lists what is waiting and hands off to the page that owns the act. */}
      <Route
        path="/manager/needs-you"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerNeedsYou />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* Index over the settings a manager owns. Same reason: five rows that
          are touched at setup and then left alone were outranking the queues
          worked every morning. */}
      <Route
        path="/manager/setup"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerShopSetup />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/flags-assist"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerAssistanceFlags />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/approvals"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerApprovals />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/team"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerTeam />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* Sprint H H2 — Manager Roldi surface (manager + admin + leadership). */}
      <Route
        path="/manager/ai"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerRoldi />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* Sprint F F2 — time-on-task threshold review surface. */}
      <Route
        path="/manager/time-thresholds"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerTimeThresholds />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* Sprint N Group A — channel management surface. */}
      <Route
        path="/manager/channels"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerChannels />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* WS-6 — Roldi review queue (approve/reject tech insights). */}
      <Route
        path="/manager/roldi-queue"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerRoldiQueue />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* WS-6 — Teach Roldi (write approved KB directly). */}
      <Route
        path="/manager/teach-roldi"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerTeachRoldi />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/* Sprint L L4 — customer management surface. */}
      <Route
        path="/manager/customers"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerCustomers />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/customers/:customerId"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerCustomerDetail />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/*
        Enrich-readiness (2026-07-22) — THE ESTIMATE DESK. Pull parts, AI labor
        hours, customer-specific stepped pricing, save + quote PDF + push to
        Enrich. Read+write is manager/admin (estimates.create is `writeAccess`);
        leadership rides along read-only (estimates.price/list are `readAccess`).
      */}
      <Route
        path="/manager/estimate"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerEstimateDesk />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />
      {/*
        Enrich-readiness (2026-07-22) — the manager-facing Enrich Sync dashboard.
        Read is manager + admin + leadership (mirrors integrationsRouter.readAccess);
        Reconcile / Send-pending are manager/admin only, enforced server-side and
        disabled in the UI for leadership.
      */}
      <Route
        path="/manager/enrich"
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin', 'leadership']}>
            <ManagerRealtimeProvider>
              <ManagerEnrichSync />
            </ManagerRealtimeProvider>
          </ProtectedRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      {/* Platform-owner console. The role guard admits any admin; the page
          itself is the real gate (it renders a refusal for a tenant admin) and
          every procedure behind it is `platformOwnerProcedure`. */}
      <Route
        path="/admin/platform"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPlatform />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/costs"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCosts />
          </ProtectedRoute>
        }
      />
      {/* The billing read. Admin-only: it names a billable unit. */}
      <Route
        path="/admin/metering"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminMetering />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/flags"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminFlags />
          </ProtectedRoute>
        }
      />
      {/*
        Sprint J J3 (2026-05-11) — leadership now has audit-log read access
        too. Sprint I caught Mike (leadership) blocked from this surface
        during his investor-demo walk. Server-side gate widened in
        adminAuditRouter via `auditViewer = roleProcedure('admin', 'leadership')`;
        the client guard mirrors that. There are no audit-log mutations,
        so widening read is a no-write change.
      */}
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute allowedRoles={['admin', 'leadership']}>
            <AdminAudit />
          </ProtectedRoute>
        }
      />
      {/*
        Enrich-readiness (2026-07-22) — the integration seam's operator surface.
        Read is manager + admin + leadership (mirrors integrationsRouter's
        `readAccess`); connection writes are admin-only and imports are
        manager/admin, both enforced server-side and reflected in the UI.
      */}
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'leadership']}>
            <AdminIntegrations />
          </ProtectedRoute>
        }
      />
      {/*
        The pricing desk — stepped markup ladders + labor rates. Read matches
        catalog.pricing's `pricingRead`; saves are manager/admin (`writeAccess`).
      */}
      <Route
        path="/admin/pricing"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'leadership']}>
            <AdminPricing />
          </ProtectedRoute>
        }
      />
      {/* Shops + their Enrich facility codes. Admin-only: a facility code
          decides which shop the truck import files every unit under. */}
      <Route
        path="/admin/shops"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminShops />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roldi"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminRoldiStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kb"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminKb />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/gold"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminGold />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/shifts"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminShifts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/agents"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminAgents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/demo"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDemoTraining />
          </ProtectedRoute>
        }
      />

      {/* Leadership */}
      <Route
        path="/leadership"
        element={
          <ProtectedRoute allowedRoles={['leadership', 'admin']}>
            <LeadershipOverview />
          </ProtectedRoute>
        }
      />
      {/* Sprint L L2 (2026-05-13) — leadership analytics surface. */}
      <Route
        path="/leadership/analytics"
        element={
          <ProtectedRoute allowedRoles={['leadership', 'admin']}>
            <LeadershipAnalytics />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
